/**
 * Firebase client initialization.
 *
 * Firebase Authentication is the identity provider. The app is initialized
 * once here and the `auth` instance is shared across the app. Auth state is
 * persisted by the SDK (browserLocalPersistence) so a page reload keeps the
 * user signed in — no tokens are stored manually.
 *
 * All config values are public by design (Firebase web config is not secret;
 * security is enforced by Firebase rules + backend token verification). They
 * are still read from VITE_ env vars so each environment points at its own
 * Firebase project.
 */

import { initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  setPersistence,
} from 'firebase/auth';

const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
const isTest = import.meta.env.MODE === 'test';
const usePlaceholderConfig = isBypass || isTest;

// Firebase rejects malformed keys at init time; use a valid-format placeholder in
// dev bypass / Vitest (CI has no .env, so real keys are absent there).
const PLACEHOLDER_FIREBASE_CONFIG = {
  apiKey: 'AIzaSy0000000000000000000000000000000',
  authDomain: 'localhost',
  projectId: 'invitecloud-test',
  storageBucket: 'invitecloud-test.appspot.com',
  messagingSenderId: '000000000000',
  appId: '1:000000000000:web:0000000000000000',
} as const;

const firebaseConfig = {
  apiKey:
    import.meta.env.VITE_FIREBASE_API_KEY ||
    (usePlaceholderConfig ? PLACEHOLDER_FIREBASE_CONFIG.apiKey : ''),
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
    (usePlaceholderConfig ? PLACEHOLDER_FIREBASE_CONFIG.authDomain : ''),
  projectId:
    import.meta.env.VITE_FIREBASE_PROJECT_ID ||
    (usePlaceholderConfig ? PLACEHOLDER_FIREBASE_CONFIG.projectId : ''),
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ||
    (usePlaceholderConfig ? PLACEHOLDER_FIREBASE_CONFIG.storageBucket : ''),
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ||
    (usePlaceholderConfig ? PLACEHOLDER_FIREBASE_CONFIG.messagingSenderId : ''),
  appId:
    import.meta.env.VITE_FIREBASE_APP_ID ||
    (usePlaceholderConfig ? PLACEHOLDER_FIREBASE_CONFIG.appId : ''),
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);

// Keep the user signed in across reloads/tabs.
void setPersistence(auth, browserLocalPersistence);

// ── OAuth providers ──────────────────────────────────────────────────────────
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();
// Microsoft is exposed through the generic OAuthProvider with the microsoft.com id.
export const microsoftProvider = new OAuthProvider('microsoft.com');

// Always show the account chooser rather than silently reusing a session.
googleProvider.setCustomParameters({ prompt: 'select_account' });
microsoftProvider.setCustomParameters({ prompt: 'select_account' });

export type ProviderId = 'google' | 'facebook' | 'microsoft';
